/**
 * Installing Ìran to the home screen.
 *
 * Two audiences this matters for: a grandmother who will never find a browser
 * bookmark, and a family on a patchy connection who need the app shell to open
 * without waiting for the network. The worker itself caches no family data —
 * see apps/web/public/sw.js.
 */

export interface InstallPrompt extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPrompt | null = null;
const listeners = new Set<(available: boolean) => void>();

function announce() {
  for (const fn of listeners) fn(deferred !== null);
}

export function onInstallAvailable(fn: (available: boolean) => void): () => void {
  listeners.add(fn);
  fn(deferred !== null);
  return () => listeners.delete(fn);
}

/** Returns true if the person accepted. Android/desktop Chromium only; iOS has no API. */
export async function promptInstall(): Promise<boolean> {
  if (!deferred) return false;
  const event = deferred;
  deferred = null;
  announce();
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome === 'accepted';
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return window.matchMedia('(display-mode: standalone)').matches || iosStandalone;
}

export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

// ---------------------------------------------------------------- updates
const updateListeners = new Set<(ready: boolean) => void>();
let updateReady = false;
let reloading = false;

/** Tells the app a newer Ìran has landed, so it can offer a reload. */
export function onUpdateReady(fn: (ready: boolean) => void): () => void {
  updateListeners.add(fn);
  fn(updateReady);
  return () => updateListeners.delete(fn);
}

function announceUpdate() {
  updateReady = true;
  for (const fn of updateListeners) fn(true);
}

/** Reload on the person's say-so — never mid-sentence, never mid-recording. */
export function applyUpdate(): void {
  if (reloading) return;
  reloading = true;
  window.location.reload();
}

export function registerServiceWorker(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPrompt;
    announce();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    announce();
  });

  if (!('serviceWorker' in navigator)) return;
  // Never on an insecurely served page. isSecureContext is the browser's own
  // answer and already covers https, localhost and 127.0.0.1.
  if (!window.isSecureContext) return;

  // A worker that took over AFTER this page loaded means a new build is live and
  // this tab is still running the old one. Say so rather than pretending. On the
  // very first visit there is no controller yet, and that is not an update.
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) announceUpdate();
  });

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) announceUpdate();
          });
        });

        // Look for a new release whenever the person comes back to the tab.
        // Without this, someone who never fully closes Ìran can sit on an old
        // build for weeks.
        let lastCheck = Date.now();
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState !== 'visible') return;
          if (Date.now() - lastCheck < 60_000) return;
          lastCheck = Date.now();
          registration.update().catch(() => undefined);
        });
      })
      .catch(() => {
        /* Installability is a nicety; never let it break the app. */
      });
  });
}

/**
 * Signing out must leave nothing behind on a shared device.
 * The worker holds no family data, but clearing is cheap insurance.
 */
export async function clearOfflineCaches(): Promise<void> {
  if (typeof caches === 'undefined') return;
  try {
    const keys = await caches.keys();
    await Promise.all(keys.map((k) => caches.delete(k)));
  } catch {
    /* ignore */
  }
}
